import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of dust motes, default 16 */
  count?: number;
  /** Mote colour (rgba), default warm tan */
  color?: string;
  /** Drift cycle duration seconds, default 7 */
  dur?: number;
}

/**
 * Sawdust — slow, low-gravity drifting motes giving a carpenter-workshop feel.
 */
const Sawdust: React.FC<Props> = ({
  count = 16,
  color = "rgba(214,180,120,0.55)",
  dur = 7,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      top: r(1) * 100,
      size: 1.5 + r(2) * 2.5,
      delay: r(3) * dur,
      d: dur * (0.7 + r(4) * 0.8),
      dx: (r(5) - 0.5) * 60,
      dy: (r(6) - 0.5) * 40,
    }),
    [count, dur]
  );

  return (
    <div style={overlayBase}>
      {parts.map((p) => {
        // Bake dx/dy into a unique keyframe per particle — CSS custom props
        // set via inline style can't be read inside @keyframes reliably.
        const kf = `oot-mote-${p.id}`;
        return (
          <React.Fragment key={p.id}>
            <style>{`
              @keyframes ${kf} {
                0%, 100% { transform: translate(0, 0); opacity: 0.15; }
                50%       { transform: translate(${p.dx}px, ${p.dy}px); opacity: 0.6; }
              }
            `}</style>
            <div
              style={{
                position: "absolute",
                left: `${p.left}%`,
                top: `${p.top}%`,
                width: p.size,
                height: p.size,
                borderRadius: "50%",
                background: color,
                animation: `${kf} ${p.d}s ease-in-out ${p.delay}s infinite`,
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Sawdust;
